import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowSquareOut, Check, Copy } from '@phosphor-icons/react';
import { useWebpages } from '@/contexts/WebpagesContext';
import { useOnboardingStatus, type McpSetupStatus } from '@/hooks/useOnboardingStatus';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { RouteLoader } from '@/components/RouteLoader';
import { StepRow } from './components/StepRow';
import { ChordProgress, type ChordPhase } from './components/ChordProgress';
import { analytics } from '@/lib/analytics';
import { pingAnyExtension } from '@/lib/extensionBridge';

const CHROME_WEB_STORE_URL =
  import.meta.env.VITE_CHROME_WEB_STORE_URL ||
  'https://chromewebstore.google.com/'; // updated once the extension is published

const MCP_DOCS_URL = 'https://www.solem.ai/docs/mcp';

// ────────────────────────────────────────────────────────────────────
// API helpers
// ────────────────────────────────────────────────────────────────────

async function getToken(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return token;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => ({}));
    throw new Error(payload.error ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

// ────────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────────

export function OnboardingStart() {
  const { user, isAuthLoading } = useWebpages();
  const navigate = useNavigate();
  const { status, loading: statusLoading, refetch } = useOnboardingStatus(!!user);

  const [extensionDetected, setExtensionDetected] = useState(false);
  const [extensionAuthedPing, setExtensionAuthedPing] = useState(false);
  const [step3Phase, setStep3Phase] = useState<ChordPhase>('idle');
  const [generatingKey, setGeneratingKey] = useState(false);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [mcpStatus, setMcpStatus] = useState<McpSetupStatus>(null);
  const [submittingMcp, setSubmittingMcp] = useState(false);
  const [modDown, setModDown] = useState(false);
  const [shiftDown, setShiftDown] = useState(false);
  const [eDown, setEDown] = useState(false);
  const startCalledRef = useRef(false);
  // Analytics: track which events have fired this mount so we never double-fire.
  // Step durations are timed from the mount instant or from each step's first
  // _viewed event, whichever is later (handles refresh-mid-flow gracefully).
  const firedRef = useRef<Set<string>>(new Set());
  const stepStartedAtRef = useRef<Record<string, number>>({});
  const mountedAtRef = useRef<number>(Date.now());
  const fire = useCallback((event: Parameters<typeof analytics.track>[0], props: Record<string, unknown> = {}) => {
    const key = `${event}:${JSON.stringify(props.step_index ?? '')}`;
    if (firedRef.current.has(key)) return;
    firedRef.current.add(key);
    analytics.track(event, props);
  }, []);

  // Platform → which modifier label / key to show + listen for
  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
  const modLabel = isMac ? '⌘' : 'Ctrl';

  // Step states
  const mcpStep1Done = mcpStatus !== null || !!status?.mcp_setup_status;
  // 1A — installed in browser (ping returns present, or already saved a page on a fresh replay)
  const extensionInstalled = extensionDetected || !!status?.page1_saved;
  // 1B — extension itself reports authed=true via PING response (v0.5.3+).
  //      A saved page also implies the extension is authed (couldn't have made
  //      the API call otherwise), so page1_saved is a fallback.
  const extensionAuthed = extensionAuthedPing || !!status?.page1_saved;
  const step1Done = extensionInstalled && extensionAuthed;
  const page1Saved = !!status?.page1_saved;
  // Step 3 stays "active" (and ChordProgress stays mounted) until the chord
  // animation completes — even if page1Saved flips true mid-animation.
  // Otherwise StepRow unmounts its children and step3Phase gets stuck at
  // 'idle', stalling the auto-advance.
  const step3Done = page1Saved && step3Phase === 'done';

  // Which step is active right now (one at a time)
  const step1Active = !step1Done;
  const step2Active = step1Done && !mcpStep1Done;
  const step3Active = step1Done && mcpStep1Done && !step3Done;

  // Stamp started_at once
  useEffect(() => {
    if (!user || startCalledRef.current) return;
    if (status?.started_at || status?.completed_at) {
      startCalledRef.current = true;
      // Returning to an in-flight onboarding (refresh / re-entry from Settings).
      if (status.started_at && !status.completed_at) {
        const elapsedH = (Date.now() - new Date(status.started_at).getTime()) / 3_600_000;
        fire('onboarding_resumed', {
          last_step_index: status.step ?? 0,
          time_since_start_h: Math.round(elapsedH * 10) / 10,
        });
      }
      return;
    }
    startCalledRef.current = true;
    api('/api/onboarding/start', { method: 'POST', body: JSON.stringify({}) })
      .then(() => {
        fire('onboarding_started', {
          device: 'desktop',
          entry_source: 'post_signup',
        });
      })
      .catch(() => { startCalledRef.current = false; });
  }, [user, status?.started_at, status?.completed_at, status?.step, fire]);

  // Step lifecycle: emit _viewed when each step becomes active, _completed when
  // each step's done flag flips true. Latched via firedRef so re-renders don't
  // re-fire (e.g., extension ping loop, advance polling tick).
  useEffect(() => {
    if (step1Active && !firedRef.current.has('onboarding_step_viewed:1')) {
      stepStartedAtRef.current.s1 = Date.now();
      fire('onboarding_step_viewed', { step_index: 1, step_name: 'extension_install' });
    }
  }, [step1Active, fire]);

  useEffect(() => {
    if (extensionAuthed && !firedRef.current.has('extension_auth_completed:')) {
      const startedAt = stepStartedAtRef.current.s1 ?? mountedAtRef.current;
      fire('extension_auth_completed', {
        seconds_to_complete: Math.max(0, Math.round((Date.now() - startedAt) / 1000)),
      });
    }
  }, [extensionAuthed, fire]);

  useEffect(() => {
    if (step1Done && !firedRef.current.has('onboarding_step_completed:1')) {
      const startedAt = stepStartedAtRef.current.s1 ?? mountedAtRef.current;
      fire('onboarding_step_completed', {
        step_index: 1, step_name: 'extension_install',
        duration_ms: Math.max(0, Date.now() - startedAt),
      });
    }
  }, [step1Done, fire]);

  useEffect(() => {
    if (step2Active && !firedRef.current.has('onboarding_step_viewed:2')) {
      stepStartedAtRef.current.s2 = Date.now();
      fire('onboarding_step_viewed', { step_index: 2, step_name: 'mcp_setup' });
    }
  }, [step2Active, fire]);

  useEffect(() => {
    if (mcpStep1Done && !firedRef.current.has('onboarding_step_completed:2')) {
      const startedAt = stepStartedAtRef.current.s2 ?? mountedAtRef.current;
      fire('onboarding_step_completed', {
        step_index: 2, step_name: 'mcp_setup',
        duration_ms: Math.max(0, Date.now() - startedAt),
      });
    }
  }, [mcpStep1Done, fire]);

  useEffect(() => {
    if (step3Active && !firedRef.current.has('onboarding_step_viewed:3')) {
      stepStartedAtRef.current.s3 = Date.now();
      fire('onboarding_step_viewed', { step_index: 3, step_name: 'first_save' });
    }
  }, [step3Active, fire]);

  useEffect(() => {
    if (step3Done && !firedRef.current.has('onboarding_step_completed:3')) {
      const startedAt = stepStartedAtRef.current.s3 ?? mountedAtRef.current;
      fire('onboarding_step_completed', {
        step_index: 3, step_name: 'first_save',
        duration_ms: Math.max(0, Date.now() - startedAt),
      });
    }
  }, [step3Done, fire]);

  // Sync local mcp state from server
  useEffect(() => {
    if (status?.mcp_setup_status && !mcpStatus) {
      setMcpStatus(status.mcp_setup_status);
    }
  }, [status?.mcp_setup_status, mcpStatus]);

  // Extension ping — runs every 2s. We also use the response to surface a
  // "shortcuts unbound" prompt if Chrome dropped the bindings (which kills
  // ⌘⇧E / ⌘⇧S for Step 3 + Page 2).
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const { present, authed } = await pingAnyExtension();
      if (cancelled) return;
      if (present) setExtensionDetected(true);
      if (authed) setExtensionAuthedPing(true);
    };
    tick();
    const interval = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Advance polling — runs while not completed.
  //
  // Two cadences:
  //   - default: 3s — light enough to be cheap, fast enough that a missed
  //     burst still resolves within a few seconds
  //   - burst: 400ms — kicks in once ChordProgress reports modifiers_held
  //     (i.e., the user is actively trying the shortcut). Lets us flash the
  //     chips green within a few hundred ms of the save landing, which is
  //     what makes the moment feel responsive.
  useEffect(() => {
    if (!user) return;
    if (status?.completed_at || status?.step === 2) return;
    const intervalMs = step3Phase === 'modifiers_held' ? 400 : 3000;
    const tick = () => {
      api<{ step: number; completed: boolean; page1_saved: boolean }>(
        '/api/onboarding/advance',
        { method: 'POST', body: JSON.stringify({}) },
      )
        .then((res) => {
          if (res.page1_saved) refetch();
        })
        .catch(() => { /* swallow; next tick will retry */ });
    };
    const interval = setInterval(tick, intervalMs);
    return () => clearInterval(interval);
  }, [user, status?.completed_at, status?.step, step3Phase, refetch]);

  // Live keypress detection for the ⌘⇧E chord. We track each key separately
  // so the chord visually fills in as the user holds the modifiers down.
  useEffect(() => {
    const isModKey = (k: string) => (isMac ? k === 'Meta' : k === 'Control');
    const down = (ev: KeyboardEvent) => {
      if (isModKey(ev.key)) setModDown(true);
      if (ev.key === 'Shift') setShiftDown(true);
      if (ev.code === 'KeyE') setEDown(true);
    };
    const up = (ev: KeyboardEvent) => {
      if (isModKey(ev.key)) setModDown(false);
      if (ev.key === 'Shift') setShiftDown(false);
      if (ev.code === 'KeyE') setEDown(false);
    };
    const clear = () => {
      setModDown(false);
      setShiftDown(false);
      setEDown(false);
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', clear);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', clear);
    };
  }, [isMac]);

  // (Eager /advance poll on chord-fire is now owned by ChordProgress via
  //  the onFire callback — see Step 3 below.)

  // Auto-advance to /welcome when ChordProgress reports `done` AND the MCP
  // choice has been made. Gating on step3Phase (rather than just page1Saved)
  // ensures the user sees the ~800ms emerald success bar before we navigate
  // away — otherwise a fast save would interrupt the feedback.
  const mcpChoiceMade = mcpStatus !== null || !!status?.mcp_setup_status;
  useEffect(() => {
    if (step3Phase === 'done' && mcpChoiceMade && !status?.completed_at) {
      navigate('/onboarding/welcome', { replace: true });
    }
  }, [step3Phase, mcpChoiceMade, status?.completed_at, navigate]);

  const handleGenerateKey = useCallback(async () => {
    setGeneratingKey(true);
    fire('mcp_setup_started', { provider: 'mcp_apikey' });
    try {
      const data = await api<{ key: string }>('/api/keys', {
        method: 'POST',
        body: JSON.stringify({ name: 'MCP — onboarding' }),
      });
      setApiKey(data.key);
    } catch (err) {
      console.error('Failed to create API key:', err);
    } finally {
      setGeneratingKey(false);
    }
  }, [fire]);

  const handleSetMcpStatus = useCallback(async (value: McpSetupStatus) => {
    if (!value || submittingMcp) return;
    setSubmittingMcp(true);
    setMcpStatus(value);
    try {
      await api('/api/onboarding/mcp-status', {
        method: 'POST',
        body: JSON.stringify({ status: value }),
      });
      const startedAt = stepStartedAtRef.current.s2 ?? mountedAtRef.current;
      fire('mcp_setup_completed', {
        provider: value === 'copied' ? 'mcp_apikey' : 'none',
        outcome: value,
        seconds_to_complete: Math.max(0, Math.round((Date.now() - startedAt) / 1000)),
      });
    } catch (err) {
      console.error('Failed to set MCP status:', err);
      setMcpStatus(null);
    } finally {
      setSubmittingMcp(false);
    }
  }, [submittingMcp, fire]);

  const handleCopyKey = useCallback(() => {
    if (!apiKey) return;
    const snippet = `Set up the Solem MCP server using the guide at\n${MCP_DOCS_URL}\n\nMy API key: ${apiKey}`;
    navigator.clipboard.writeText(snippet);
    setCopied(true);
    void handleSetMcpStatus('copied');
    setTimeout(() => setCopied(false), 2200);
  }, [apiKey, handleSetMcpStatus]);

  const [skipError, setSkipError] = useState<string | null>(null);
  const skipInFlightRef = useRef(false);

  // Hard-skip path for the shortcut step. Reached only when the chord can't
  // fire (unbound, conflict, system-level interception we can't work around).
  // Marks onboarding complete and lands the user in /saved; the wizard never
  // shows again. Analytics fires ONLY on confirmed server success — otherwise
  // we'd record skip events for users who never actually got skipped, and
  // ProtectedRoute could bounce them back into the wizard. The in-flight ref
  // suppresses double-click double-emission (server is idempotent; analytics
  // is not).
  const handleSkipShortcut = useCallback(async () => {
    if (skipInFlightRef.current) return;
    skipInFlightRef.current = true;
    setSkipError(null);
    try {
      await api('/api/onboarding/skip', {
        method: 'POST',
        body: JSON.stringify({ skipped_at_step: 3, reason: 'shortcut_did_not_fire' }),
      });
    } catch (err) {
      console.error('Failed to mark onboarding skipped:', err);
      setSkipError("We couldn't skip just now. Try again in a moment.");
      skipInFlightRef.current = false;
      return;
    }
    analytics.track('onboarding_skipped', {
      skipped_at_step: 3,
      step_name: 'first_save',
      reason: 'shortcut_did_not_fire',
    });
    navigate('/saved', { replace: true });
  }, [navigate]);

  // ── Render guards ─────────────────────────────────────────────────
  // Neutral loader (no /saved skeleton) — at this point we don't yet know
  // whether this user is mid-wizard or already complete and about to bounce.
  if (isAuthLoading || statusLoading) {
    return <RouteLoader />;
  }

  // Already complete → bounce to /saved (e.g. someone manually hits /onboarding)
  if (status?.completed_at) {
    return <Navigate to="/saved" replace />;
  }

  // ── UI ────────────────────────────────────────────────────────────
  return (
    <>
      <main className="min-h-screen bg-white">
        <Helmet>
          <title>Set up Solem</title>
        </Helmet>

      <div className="mx-auto max-w-2xl px-6 pt-20 pb-24">
        <header className="mb-12">
          <p className="mb-2 text-sm font-medium uppercase tracking-wider text-neutral-400">
            Welcome
          </p>
          <h1 className="text-2xl font-light tracking-tight text-neutral-900">
            Set up Solem
          </h1>
          <p className="mt-2 text-base font-light text-neutral-600/80">
            Three quick steps. Takes about two minutes.
          </p>
        </header>

        {page1Saved && !mcpChoiceMade && (
          <div className="mb-4 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Page saved! One more thing — pick your AI agent option below, then we'll
            move you to the welcome screen.
          </div>
        )}

        <div className="rounded-sm border border-neutral-200 bg-neutral-50 px-6">
          {/* Step 1 — Install + sign in to extension (no nested radios) */}
          <StepRow
            index={1}
            title="Install the Chrome extension"
            done={step1Done}
            active={step1Active}
            summary={<span>Extension installed and signed in — you're good.</span>}
          >
            <p className="mb-4 text-sm text-neutral-600">
              Solem saves any page in one keystroke. The extension is how it all works.
            </p>
            {!extensionInstalled ? (
              <div className="flex flex-wrap items-center gap-3">
                <Button asChild className="rounded-sm">
                  <a
                    href={CHROME_WEB_STORE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2"
                  >
                    Install from Chrome Web Store
                    <ArrowSquareOut size={14} weight="bold" />
                  </a>
                </Button>
                <span className="text-xs text-neutral-400">
                  We'll detect it automatically once you install.
                </span>
              </div>
            ) : (
              <p className="text-sm text-neutral-600">
                Extension detected. Pin Solem and click the icon to sign in if
                you haven't already — we'll catch it automatically.
              </p>
            )}
          </StepRow>

          {/* Step 2 — MCP */}
          <StepRow
            index={2}
            title="Connect your AI agent (Claude Code, Cursor, OpenClaw…)"
            done={mcpStep1Done}
            active={step2Active}
            summary={
              mcpStatus === 'no_compatible_ai'
                ? <ReassuranceCallout />
                : <span>API key created — paste it into your AI agent anytime.</span>
            }
          >
            {apiKey ? (
              <ApiKeyBlock apiKey={apiKey} onCopy={handleCopyKey} copied={copied} />
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-neutral-600">
                  Use any MCP-compatible AI app (Claude Desktop, Cursor, Claude Code, OpenClaw)
                  and we'll plug Solem in.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    onClick={handleGenerateKey}
                    disabled={generatingKey}
                    className="rounded-sm"
                  >
                    {generatingKey ? 'Generating…' : 'Generate API key'}
                  </Button>
                  <span className="text-xs text-neutral-400">— or —</span>
                  <button
                    type="button"
                    onClick={() => void handleSetMcpStatus('no_compatible_ai')}
                    disabled={submittingMcp}
                    className="text-sm text-neutral-600 underline decoration-neutral-300 underline-offset-4 hover:text-neutral-900"
                  >
                    I don't have an MCP-compatible AI
                  </button>
                </div>
              </div>
            )}
          </StepRow>

          {/* Step 3 — Save this page */}
          <StepRow
            index={3}
            title="Save this page to finish setup"
            done={step3Done}
            active={step3Active}
          >
            <ChordProgress
              keys={[modLabel, '⇧', 'E']}
              pressed={[modDown, shiftDown, eDown]}
              saved={page1Saved}
              targetKeyName="E"
              onFire={() => {
                // Eager /advance the moment modifiers are observed — pairs
                // with the 400ms burst-poll cadence in the polling effect.
                api<{ page1_saved: boolean }>('/api/onboarding/advance', {
                  method: 'POST',
                  body: JSON.stringify({}),
                })
                  .then((res) => { if (res.page1_saved) refetch(); })
                  .catch(() => { /* swallow */ });
              }}
              onPhaseChange={setStep3Phase}
              onSkip={handleSkipShortcut}
              idleHint="Press the chord above to save this page."
            />
            {skipError && (
              <p className="mt-2 text-xs text-red-600">{skipError}</p>
            )}
          </StepRow>
        </div>
      </div>
      </main>
    </>
  );
}

// ── Local helpers ──────────────────────────────────────────────────

function ApiKeyBlock({
  apiKey,
  onCopy,
  copied,
}: {
  apiKey: string;
  onCopy: () => void;
  copied: boolean;
}) {
  const snippet = `Set up the Solem MCP server using the guide at\n${MCP_DOCS_URL}\n\nMy API key: ${apiKey}`;
  return (
    <div className="space-y-3">
      <p className="text-sm text-neutral-600">
        Copy this and paste it into your AI agent. It will read the docs and configure
        itself.
      </p>
      <div className="relative">
        <pre className="overflow-x-auto rounded-sm border border-neutral-200 bg-white p-4 font-mono text-xs leading-relaxed text-neutral-800">
          <code>{snippet}</code>
        </pre>
        <button
          type="button"
          onClick={onCopy}
          className="absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-sm border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-100"
        >
          {copied ? (
            <>
              <Check size={12} weight="bold" />
              Copied
            </>
          ) : (
            <>
              <Copy size={12} weight="bold" />
              Copy
            </>
          )}
        </button>
      </div>
      <p className="text-xs text-neutral-400">
        Keep this key safe — you can revoke or create more from Settings → API keys.
      </p>
    </div>
  );
}

function ReassuranceCallout() {
  return (
    <div className="rounded-sm border border-neutral-200 bg-white px-4 py-3">
      <p className="text-sm font-medium text-neutral-900">
        No worries — Solem has built-in AI chat.
      </p>
      <p className="mt-1 text-sm leading-relaxed text-neutral-600">
        Ask questions about your saved pages right inside the app, no external agent
        needed. You can connect an AI agent later from Settings → AI agents.
      </p>
    </div>
  );
}
